/* eslint-disable @typescript-eslint/no-explicit-any */
import { EditableComponent, ResponsiveGrid } from '@adobe/aem-react-editable-components';
import {
  AEMReactCompMap,
  TabContainer as TabContainerMolecule,
  updateAEMCQKeys,
  useEditorMode,
} from '@marriott/mi-ui-library';
import clsx from 'clsx';
import { FC, lazy, ReactElement, Suspense, useEffect, useState } from 'react';
import { TabProps, TabContainerProps, TabDetails } from './TabContainer.types';

// eslint-disable-next-line @nx/enforce-module-boundaries
import { getOffersComponent, buildItemProps } from '../../../../mi-offers-components/src/lib/helper';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { buildCategoryItemProps, getCategoryComponent } from '../../../../mi-category-components/src/lib/helper';
import { getAwpComponent } from '../../utils/getAwpComponent';
import { getWCMModeFlagValue } from '@marriott/shared/mi-helper-utils';

export const TabContainerConfig = {
  emptyLabel: 'TabContainer',
  isEmpty: () => true,
  resourceType: `${process.env['NEXT_PUBLIC_AEM_SITE']}/components/content/tabcontainer`,
};

export const TabContainerComp: FC<TabProps> = (props: TabProps) => {
  const {
    header,
    headerTag,
    subHeader,
    trackingProperties,
    styleclass,
    componentId,
    pagePath,
    itemPath,
    isAuthorMode,
    contentBlockAlignment,
    headerFontSize,
    subFontSize,
    subHeaderTag,
    descriptionText,
    descFontSize,
    tabDetails,
    layoutContainer,
  } = props;

  const updatedProps = updateAEMCQKeys(props);
  const cqItems = updatedProps['cqItems'];
  const wcmModeDisabled = getWCMModeFlagValue();

  const mapper = AEMReactCompMap(props?.allowedComponents);

  const [isEditMode, setEditMode] = useState(false);
  const { isInEditor } = useEditorMode();
  useEffect(() => {
    const pageWcmMode = document.getElementsByTagName('html')[0];
    const updateStyleComponent = document.getElementById(`${componentId}__slides`);
    const observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        if (mutation.attributeName === 'class') {
          const currWcmMode = pageWcmMode?.getAttribute('class');
          if (currWcmMode?.includes('Edit')) {
            updateStyleComponent?.classList.add('glide__slides_authoring');
            setEditMode(true);
          } else if (currWcmMode?.includes('Preview')) {
            updateStyleComponent?.classList.remove('glide__slides_authoring');
            setEditMode(false);
          }
        }
      });
    });
    observer.observe(pageWcmMode, { attributes: true });

    return () => {
      observer.disconnect();
    };
  }, []);

  // This function is used to render the component in authoring mode authorComponentWrapper
  const authorComponentWrapper = (index: number) => {
    return (
      <ResponsiveGrid
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        pagePath={pagePath}
        itemPath={`${itemPath}/${tabDetails[index].nodeName}`}
        columnCount="12"
        gridClassNames={''}
        customClassName={''}
        config={{
          isEmpty: () => true,
          resourceType: 'mi-aem-common-spa/components/container',
        }}
        isInEditor={isInEditor}
      />
    );
  };

  // This function is used to render the component in end-user mode
  const publishComponentWrapper = (jsonData: any, idx: number): ReactElement | null => {
    delete jsonData.tabDetails;
    const componentsArray: JSX.Element[] = [];
    // eslint-disable-next-line no-prototype-builtins
    if (jsonData?.hasOwnProperty(`${tabDetails[idx].nodeName}`)) {
      const page = jsonData[`${tabDetails[idx].nodeName}`];
      const pageItems = page[':items'];
      const pageItemsOrder: string[] = page[':itemsOrder'];

      pageItemsOrder?.forEach(itemKey => {
        if (Object.prototype.hasOwnProperty.call(pageItems, itemKey)) {
          const item = pageItems[itemKey];
          const itemType = item[':type']?.split('/').pop();
          if (Object.prototype.hasOwnProperty.call(mapper, itemType)) {
            const innerComp = mapper[itemType];

            const Component = lazy(() =>
              // TODO: The below hardcoded path would be removed as a part of tech debt story.
              props?.componentPath === 'mi-awp-components'
                ? getAwpComponent(innerComp)
                : props?.componentPath === 'mi-luxury-components' &&
                  (itemType === 'round-up' || itemType === 'linkbanner' || itemType === 'cardcarouselcontainer')
                ? import(`../../../../mi-luxury-components/src/organisms/${innerComp}/index`).then(module => ({
                    default: module[`${innerComp}`],
                  }))
                : props?.componentPath === 'mi-luxury-components' &&
                  (itemType === 'wysiwyg' ||
                    itemType === 'header-text-cta' ||
                    itemType === 'carouselgallery' ||
                    itemType === 'cardhorizontalfeature')
                ? import(`../../../../mi-luxury-components/src/molecules/${innerComp}/index`).then(module => ({
                    default: module[`${innerComp}`],
                  }))
                : props?.componentPath === 'mi-offers-components'
                ? getOffersComponent(innerComp, itemType, item[':type'])
                : props?.componentPath === 'mi-leisure-components' && innerComp === 'DynamicPropertyCards'
                ? import(`../../../../mi-leisure-components/src/organisms/${innerComp}/index`).then(module => ({
                    default: module[`${innerComp}`],
                  }))
                : props?.componentPath === 'mi-category-components'
                ? getCategoryComponent(innerComp, item[':type'])
                : import(`../${innerComp}/index`).then(module => ({
                    default: module[`${innerComp}`],
                  }))
            );

            const itemProps =
              props?.componentPath === 'mi-offers-components'
                ? buildItemProps(item, itemType, innerComp, props)
                : props?.componentPath === 'mi-leisure-components' && innerComp === 'DynamicPropertyCards'
                ? { model: { ...item }, ...item, componentPath: props?.componentPath, ...props?.offersData }
                : props?.componentPath === 'mi-category-components'
                ? buildCategoryItemProps(item, innerComp, props)
                : { model: { ...item }, ...item, componentPath: props?.componentPath };

            componentsArray.push(<Component {...itemProps} />);
          }
        }
      });
      if (componentsArray && componentsArray.length > 0) {
        return (
          <div>
            {componentsArray.map((child, index) => (
              <Suspense fallback={<></>} key={index}>
                {child}
              </Suspense>
            ))}
          </div>
        );
      } else {
        return null;
      }
    }
    return null;
  };

  return (
    <TabContainerMolecule
      componentId={componentId}
      subHeader={subHeader}
      styleclass={styleclass}
      trackingProperties={trackingProperties}
      header={header}
      headerTag={headerTag}
      isAuthorMode={isAuthorMode}
      contentBlockAlignment={contentBlockAlignment}
      headerFontSize={headerFontSize}
      subFontSize={subFontSize}
      subHeaderTag={subHeaderTag}
      descriptionText={descriptionText}
      descFontSize={descFontSize}
      tabDetails={tabDetails}
      layoutContainer={layoutContainer}
      isEditMode={isEditMode}
      componentPath={props?.componentPath}
      flexibleTabWidth={props?.flexibleTabWidth}
      tabsPerViewInDesktop={props?.tabsPerViewInDesktop}
    >
      {isAuthorMode && !(wcmModeDisabled && props?.componentPath === 'mi-luxury-components')
        ? Array.from({ length: tabDetails?.length }, (_, i) => authorComponentWrapper(i))
        : tabDetails?.map((_tab: TabDetails, idx) => publishComponentWrapper(cqItems, idx))}
    </TabContainerMolecule>
  );
};

export const TabEditableComponent = (props: any) => {
  const { model } = props;
  const cqType = model?.cqType || model?.[':type'];
  return (
    <EditableComponent config={TabContainerConfig} {...props}>
      <TabContainerComp {...props} componentName={cqType?.split('/')?.pop()} />
    </EditableComponent>
  );
};

export const TabContainer: FC<TabContainerProps> = (props: TabContainerProps) => {
  return (
    <div className={clsx(props?.model?.styleclass)} data-testid="tab-container">
      <TabEditableComponent {...props} />
    </div>
  );
};
